/* eslint-disable no-console */
import React, { useState, useEffect } from 'react';

import { Box, Button, Chip, CircularProgress, Dialog, DialogContent, IconButton, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { AssignmentOutlined, CheckCircle, Close } from '@material-ui/icons';
import { AttachFileOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import BackButton from '../../components/common/BackButton';
import MaterialsList from '../../components/observation/MaterialsList';
import TechnologistEvaluationModal from '../../components/observation/TechnologistEvaluationModal';
import QuizAccessGuard from '../../components/quizAccessGuard/QuizAccessGuard';
import VideoTracker from '../../components/video/VideoTracker';
import { MODALITY_OPTIONS_LABELS, MODALITY_ABBREVIATIONS } from '../../constants/observations';
import { useAppContext } from '../../contexts/AppContext';
import QuizCollectionService from '../../services/quizCollection.service';

const quizCollectionService = new QuizCollectionService();

const useStyles = makeStyles((theme) => ({
  backButton: {
    '&:hover': {
      backgroundColor: '#f0f9ff',
    },
    backgroundColor: 'transparent',
    border: '1px solid #4FC3F7',
    color: '#4FC3F7',
  },
  classHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  introDescription: {
    color: '#666',
    fontSize: 16,
    lineHeight: 1.5,
    marginBottom: 50,
  },
  introModal: {
    '& .MuiBackdrop-root': {
      backgroundColor: 'white',
      width: '100%',
    },
    alignItems: 'center',
    border: '1px solid #4FC3F7',
    boxShadow: '0px 0px 50px 0px rgba(0, 0, 0, 0.1)',
  },
  introModalPaper: {
    background: 'white',
    borderRadius: 8,
    boxShadow: '0px 0px 50px 0px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '80px 50px 60px',
    textAlign: 'center',
    width: 740,
  },
  introTitle: {
    color: '#263238',
    fontSize: '28px',
    fontWeight: 'bold',
    marginBottom: 20,
  },
  mainContainer: {
    display: 'grid',
    fontFamily: 'Roboto !important',
    gap: 20,
    gridTemplateColumns: '1fr 438px',
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
  },
  mainSide: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: 16,
    maxWidth: 'calc(100vw - 420px - 120px)',
    padding: '50px 50px 70px 70px',
  },
  materialsSidebar: {
    backgroundColor: 'white',
    boxShadow: '0px 0px 10px 0px #0000001A',
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    overflow: 'auto',
    padding: 20,
    position: 'fixed',
    right: 0,
    top: 64,
    transform: 'translateX(0)',
    transition: 'transform 0.3s ease-in-out',
    width: 350,
    zIndex: 1300,
  },
  materialsSidebarHidden: {
    transform: 'translateX(100%)',
  },
  message: {
    fontSize: 18,
    fontWeight: 500,
    lineHeight: 1.6,
    marginBottom: 16,
  },
  navigationButton: {
    width: 132,
  },
  navigationButtons: {
    display: 'flex',
    gap: 16,
    justifyContent: 'space-between',
    marginTop: 32,
  },
  observationInfo: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    height: '100%',
    justifyContent: 'center',
    maxWidth: 'calc(100% - 60px - 9px - 14px - 18px)',
  },
  observationItem: {
    '&:hover': {
      backgroundColor: '#e3f2fd',
    },
    alignItems: 'flex-start',
    backgroundColor: '#F5F5F5',
    border: '1px solid #E0E0E0',
    borderRadius: 8,
    cursor: 'pointer',
    display: 'flex',
    flexGrow: 1,
    gap: 12,
    padding: 9,
    paddingRight: 18,
    position: 'relative',
    transition: 'all 0.2s ease',
  },
  observationItemActive: {
    borderColor: '#4FC3F7',
  },
  observationItemCompleted: {
    borderColor: '#4FC3F7',
  },
  observationList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: 16,
  },
  observationSubtitle: {
    color: '#99A9C5',
    fontSize: 13,
  },
  observationThumbnail: {
    alignItems: 'center',
    backgroundColor: '#4FC3F7',
    borderRadius: '6px',
    color: 'white',
    display: 'flex',
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 'bold',
    height: 50,
    justifyContent: 'center',
    width: 60,
  },
  observationTitle: {
    color: '#172B4D',
    fontSize: 13,
    fontWeight: 'bold',
    overflow: 'hidden',
    textAlign: 'left',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  observationsSidebar: {
    display: 'flex',
    flexDirection: 'column',
    padding: 20,
    width: 380,

    [theme.breakpoints.up('lg')]: {
      height: 'calc(100vh - 110px)',
    },
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    height: '100vh',
    left: 0,
    position: 'fixed',
    top: 0,
    width: '100vw',
    zIndex: 1200,
  },
  progressBar: {
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
    minHeight: 4,
    width: '100%',
  },
  progressFill: {
    backgroundColor: '#4FC3F7',
    borderRadius: 4,
    height: '100%',
    transition: 'width 0.3s ease',
  },
  progressHeader: {
    alignItems: 'center',
    display: 'flex',
    fontSize: 18,
    fontWeight: 600,
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  quizBadge: {
    backgroundColor: '#2AAAE2',
    borderRadius: 20,
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
    letterSpacing: '25%',
    lineHeight: '100%',
    padding: '5px 16px',
  },
  seeLink: {
    '&:hover': {
      textDecoration: 'underline',
    },
    alignItems: 'center',
    color: '#4FC3F7',
    cursor: 'pointer',
    display: 'flex',
    fontSize: 14,
    fontWeight: 500,
    textDecoration: 'none',
  },
  statusIcon: {
    borderRadius: '50%',
    height: 12,
    marginRight: 8,
    width: 12,
  },
  summaryContainer: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: 16,
  },
  summaryText: {
    color: '#666',
    fontSize: 14,
    lineHeight: 1.5,
    textAlign: 'justify',
  },
  summaryTitle: {
    color: '#666',
    fontSize: 16,
    fontWeight: 'bold',
  },
  timelineDot: {
    alignItems: 'center',
    backgroundColor: '#2AAAE2',
    borderRadius: '50%',
    color: 'white',
    display: 'flex',
    fontSize: 10,
    height: 6,
    justifySelf: 'center',
    width: 6,
  },
  timelineLine: {
    backgroundColor: '#2AAAE2',
    height: '160%',
    justifySelf: 'center',
    minHeight: '25px',
    width: 1,
  },
  timelineNumber: {
    alignItems: 'center',
    backgroundColor: '#2AAAE2',
    borderRadius: '50%',
    color: 'white',
    display: 'flex',
    fontSize: 10,
    height: 20,
    justifyContent: 'center',
    justifySelf: 'center',
    width: 20,
  },
  timelineSection: {
    alignItems: 'center',
    display: 'grid',
    gridTemplateRows: '1fr 20px 1fr',
    height: '100%',
    justifyContent: 'center',
    width: 20,
  },
  videoPlayer: {
    borderRadius: 16,
    maxWidth: 'calc(100vw - 420px - 120px)',
    minHeight: 400,
    width: '100%',
  },
}));

const QuizView = () => {
  const classes = useStyles();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAppContext();

  const TRACKING_MODES = {
    PREVIEW: 'preview',
    TRACKING: 'tracking',
  };

  const [loading, setLoading] = useState(true);
  const [quizCollection, setQuizCollection] = useState(null);
  const [observations, setObservations] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [currentObservationIndex, setCurrentObservationIndex] = useState(0);
  const [showIntroModal, setShowIntroModal] = useState(true);
  const [completedObservations, setCompletedObservations] = useState(new Set());
  const [showMaterialsSidebar, setShowMaterialsSidebar] = useState(false);
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [evaluationObservationId, setEvaluationObservationId] = useState(null);
  const [mode, setMode] = useState(null);

  const fetchQuizCollection = async () => {
    try {
      const response = await quizCollectionService.getQuizCollectionById(id);
      setQuizCollection(response.quizCollection);
    } catch (err) {
      console.error('Error fetching quiz collection:', err);
      enqueueSnackbar('Failed to load quiz collection', { variant: 'error' });
    }
  };

  const loadProgress = async () => {
    if (mode === TRACKING_MODES.TRACKING && currentUser?.id) {
      try {
        const response = await quizCollectionService.getTechnologistProgress(id, currentUser.id);
        if (response.progress) {
          setCurrentObservationIndex(response.progress.currentObservationIndex || 0);
          setCompletedObservations(new Set(response.progress.completedObservations || []));
        }
      } catch (err) {
        console.error('Error loading progress:', err);
      }
    }
  };

  const saveProgress = async (progressData) => {
    if (mode === TRACKING_MODES.TRACKING && currentUser?.id) {
      try {
        await quizCollectionService.updateQuizProgress(id, currentUser.id, progressData);
      } catch (err) {
        console.error('Error saving progress:', err);
      }
    }
  };

  const startQuizProgress = async () => {
    if (mode === TRACKING_MODES.TRACKING && currentUser?.id) {
      try {
        await quizCollectionService.startQuizProgress(id, currentUser.id);
      } catch (err) {
        console.error('Error starting quiz progress:', err);
      }
    }
  };

  const fetchObservations = async () => {
    try {
      const response = await quizCollectionService.getQuizCollectionObservations(id);
      setObservations(response.observations || []);
    } catch (err) {
      console.error('Error fetching observations:', err);
      enqueueSnackbar('Failed to load quiz observations', { variant: 'error' });
    }
  };

  const fetchMaterials = async () => {
    try {
      const response = await quizCollectionService.getQuizCollectionMaterials(id);
      setMaterials(response.materials || []);
    } catch (err) {
      console.error('Error fetching materials:', err);
      enqueueSnackbar('Failed to load quiz materials', { variant: 'error' });
    }
  };

  useEffect(() => {
    if (mode) {
      loadProgress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const currentObservation = observations[currentObservationIndex];
  const progressPercentage = observations.length > 0 ? (completedObservations.size / observations.length) * 100 : 0;
  const currentObservationMaterials = currentObservation?.materials || [];

  const handleStartQuiz = async () => {
    setShowIntroModal(false);
    if (mode === TRACKING_MODES.TRACKING) {
      await startQuizProgress();
    }
  };

  const handleObservationClick = (index) => {
    setCurrentObservationIndex(index);
    if (mode === TRACKING_MODES.TRACKING) {
      saveProgress({ currentObservationIndex: index });
    }
  };

  const handlePreviousObservation = () => {
    if (currentObservationIndex > 0) {
      const newIndex = currentObservationIndex - 1;

      setCurrentObservationIndex(newIndex);

      if (mode === TRACKING_MODES.TRACKING) {
        saveProgress({ currentObservationIndex: newIndex });
      }
    }
  };

  const handleNextObservation = () => {
    if (currentObservationIndex < observations.length - 1) {
      const newIndex = currentObservationIndex + 1;

      setCurrentObservationIndex(newIndex);

      if (mode === TRACKING_MODES.TRACKING) {
        saveProgress({ currentObservationIndex: newIndex });
      }
    }
  };

  const handleVideoEnded = async () => {
    if (!currentObservation) return;

    if (!currentObservation.hasEvaluation) {
      markObservationCompleted();

      return;
    }

    // Check if evaluation is already completed for tracking mode
    if (mode === TRACKING_MODES.TRACKING) {
      try {
        const evaluationData = await quizCollectionService.getEvaluationByObservationId(currentObservation.id);
        const hasAllAnswers = evaluationData.questions && evaluationData.questions.every((q) => evaluationData.answers && evaluationData.answers[q.id]);

        if (hasAllAnswers) {
          markObservationCompleted();

          return;
        }
      } catch (err) {
        console.error('Error checking evaluation status:', err);
      }
    }

    setEvaluationObservationId(currentObservation.id);
    setShowEvaluationModal(true);
  };

  const showQuestionsModal = () => {
    setEvaluationObservationId(currentObservation.id);
    setShowEvaluationModal(true);
  };

  const finishQuiz = async () => {
    try {
      await quizCollectionService.completeQuiz(id, currentUser.id);
      enqueueSnackbar('Quiz completed!', { variant: 'success' });
    } catch (err) {
      console.error('Error completing quiz:', err);
    }
  };

  const markObservationCompleted = async () => {
    const newCompleted = new Set(completedObservations);

    newCompleted.add(currentObservationIndex);
    setCompletedObservations(newCompleted);

    if (mode === TRACKING_MODES.TRACKING && currentObservation) {
      try {
        await quizCollectionService.completeObservation(id, currentUser.id, currentObservation.id, {
          completedAt: new Date().toISOString(),
          videoWatched: true,
        });

        await saveProgress({
          completedObservations: Array.from(newCompleted),
          currentObservationIndex,
        });
      } catch (err) {
        console.error('Error saving observation completion:', err);
      }
    }

    if (currentObservationIndex < observations.length - 1) {
      setTimeout(() => {
        handleNextObservation();
      }, 1000);
    } else if (mode === TRACKING_MODES.TRACKING) {
      finishQuiz();
    }
  };

  const handleEvaluationModalClose = async (continueEvaluation = false) => {
    if (!continueEvaluation) return;

    setShowEvaluationModal(false);
    setEvaluationObservationId(null);

    if (mode === TRACKING_MODES.TRACKING) {
      const newCompleted = new Set(completedObservations);
      newCompleted.add(currentObservationIndex);

      try {
        await saveProgress({
          completedObservations: Array.from(newCompleted),
          currentObservationIndex,
        });
      } catch (err) {
        console.error('Error saving progress after evaluation:', err);
      }
    }

    markObservationCompleted();
  };

  const handleAccessGranted = async (accessMode) => {
    setMode(accessMode);

    try {
      if (id) {
        await fetchQuizCollection();
        await fetchObservations();
        await fetchMaterials();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAccessDenied = () => {
    setLoading(false);
  };

  return (
    <QuizAccessGuard quizId={id} onAccessGranted={handleAccessGranted} onAccessDenied={handleAccessDenied}>
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress />
        </Box>
      )}

      {quizCollection && showIntroModal && (
        <Dialog open={showIntroModal} className={classes.introModal} PaperProps={{ className: classes.introModalPaper }}>
          <DialogContent>
            <Chip label="Learning Module" size="small" className={`${classes.quizBadge} mb-2`} />

            <Typography className={classes.introTitle}>{quizCollection.title}</Typography>

            <Typography className={classes.introDescription}>{quizCollection.description}</Typography>

            <Button variant="contained" color="primary" onClick={handleStartQuiz} style={{ padding: '10px 30px' }}>
              Start Module
            </Button>
          </DialogContent>
        </Dialog>
      )}

      {!loading && quizCollection && !showIntroModal && (
        <div className={classes.mainContainer}>
          {/* Materials Sidebar */}
          {currentObservationMaterials.length > 0 && (
            <>
              {showMaterialsSidebar && <Box className={classes.overlay} onClick={() => setShowMaterialsSidebar(false)} />}
              <Box className={`${classes.materialsSidebar} ${!showMaterialsSidebar ? classes.materialsSidebarHidden : ''}`}>
                <Box
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 16,
                  }}>
                  <Typography
                    style={{
                      alignItems: 'center',
                      display: 'flex',
                      fontSize: 20,
                      fontWeight: 'bold',
                      gap: 6,
                    }}>
                    Case Materials
                  </Typography>

                  <IconButton size="small" onClick={() => setShowMaterialsSidebar(false)} style={{ color: '#666' }}>
                    <Close />
                  </IconButton>
                </Box>

                <MaterialsList materials={currentObservationMaterials} selectedMaterials={currentObservationMaterials} />
              </Box>
            </>
          )}

          {/* Evaluation Modal */}
          <TechnologistEvaluationModal
            open={showEvaluationModal}
            onClose={handleEvaluationModalClose}
            observationId={evaluationObservationId}
            disableSaving={mode === TRACKING_MODES.PREVIEW}
            previewMode={mode === TRACKING_MODES.PREVIEW}
          />

          {/* Main Content */}
          <Box className={classes.mainSide}>
            {currentObservation ? (
              <>
                <Typography align="left" className={classes.message}>
                  The module will take you through a set of videos, each of which is followed by questions. Please complete all of the videos and questions as they are presented to
                  you.
                </Typography>

                <Box className={classes.classHeader}>
                  <Box display="flex" alignItems="center" gridGap={12}>
                    {mode === TRACKING_MODES.PREVIEW && <BackButton route="/quizzes" goBackHistory marginBottom={0} color="#000000" />}

                    <Chip label={`Video ${currentObservationIndex + 1}`} size="small" className={`${classes.quizBadge}`} />
                  </Box>

                  <Box display="flex" alignItems="center" gridGap={12}>
                    {currentObservationMaterials.length > 0 && (
                      <Typography className={classes.seeLink} onClick={() => setShowMaterialsSidebar(!showMaterialsSidebar)}>
                        <AttachFileOutlined size="small" />
                        Case Materials
                      </Typography>
                    )}

                    {mode === TRACKING_MODES.PREVIEW && currentObservation.hasEvaluation && (
                      <Typography title="(Preview Only)" className={classes.seeLink} style={{ color: '#172B4D', gap: 6 }} onClick={showQuestionsModal}>
                        <AssignmentOutlined size="small" />
                        Jump to Questions
                      </Typography>
                    )}
                  </Box>
                </Box>

                <VideoTracker
                  src={currentObservation.videoUrl}
                  threshold={95}
                  videoProps={{
                    className: classes.videoPlayer,
                    controls: true,
                    onEnded: handleVideoEnded,
                  }}>
                  <track
                    kind="captions"
                    src={currentObservation.vttSubtitles ? `data:text/vtt;base64,${btoa(currentObservation.vttSubtitles)}` : ''}
                    label="English"
                    srcLang="en"
                    default={!!currentObservation.vttSubtitles}
                  />
                  Your browser does not support the video tag.
                </VideoTracker>

                {currentObservation.summary && (
                  <Box className={classes.summaryContainer}>
                    <Typography className={classes.summaryTitle}>Summary</Typography>
                    <Typography className={classes.summaryText}>{currentObservation.summary}</Typography>
                  </Box>
                )}

                <Box className={classes.navigationButtons}>
                  <Button className={classes.navigationButton} variant="outlined" color="primary" onClick={handlePreviousObservation} disabled={currentObservationIndex === 0}>
                    Back
                  </Button>

                  <Button
                    className={classes.navigationButton}
                    variant="contained"
                    color="primary"
                    onClick={handleNextObservation}
                    disabled={currentObservationIndex === observations.length - 1}>
                    Next
                  </Button>
                </Box>
              </>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                <Typography variant="h6" color="textSecondary">
                  No observations available in this quiz
                </Typography>
              </Box>
            )}
          </Box>

          {/* Observations Sidebar */}
          <Box className={classes.observationsSidebar}>
            <Box className={classes.progressHeader}>
              <Typography style={{ fontSize: '18px', fontWeight: 600 }}>Progress</Typography>
            </Box>

            <div className={classes.progressBar}>
              <div className={classes.progressFill} style={{ width: `${progressPercentage}%` }} />
            </div>

            <Box display="flex" justifyContent="space-between" mt={1}>
              <Typography variant="body2">0%</Typography>
              <Typography variant="body2">100%</Typography>
            </Box>

            <Box className={classes.observationList}>
              {observations.map((observation, index) => {
                const isCompleted = completedObservations.has(index);
                const isCurrent = index === currentObservationIndex;
                const previousOneIsCompleted = completedObservations.has(index - 1);
                const nextOneIsCompleted = completedObservations.has(index + 1);
                const nextOneIsCurrent = index + 1 === currentObservationIndex;
                const isFirst = index === 0;
                const isLast = index === observations.length - 1;

                return (
                  <Box key={observation.id} display="flex" alignItems="center" gridGap={12}>
                    <Box height="100%" className={classes.timelineSection}>
                      <Box
                        className={classes.timelineLine}
                        style={{ visibility: isFirst || !previousOneIsCompleted || (previousOneIsCompleted && !isCurrent && !isCompleted) ? 'hidden' : 'visible' }}
                      />

                      {isCurrent ? (
                        <Box height="100%" className={classes.timelineNumber}>
                          {index + 1}
                        </Box>
                      ) : (
                        <Box height="100%" className={classes.timelineDot} style={{ visibility: !isCurrent && isCompleted ? 'visible' : 'hidden' }} />
                      )}

                      <Box
                        className={classes.timelineLine}
                        style={{ visibility: isLast || !isCompleted || (isCompleted && !nextOneIsCurrent && !nextOneIsCompleted) ? 'hidden' : 'visible' }}
                      />
                    </Box>

                    <Box
                      className={`${classes.observationItem} ${index === currentObservationIndex ? classes.observationItemActive : ''} ${
                        completedObservations.has(index) ? classes.observationItemCompleted : ''
                      }`}
                      onClick={() => handleObservationClick(index)}>
                      <Box className={classes.observationThumbnail}>{MODALITY_ABBREVIATIONS[observation.modality] || 'N/A'}</Box>

                      <Box className={classes.observationInfo}>
                        <Typography className={classes.observationTitle} title={observation.examName}>
                          {observation.examName || 'Case Study'}
                        </Typography>
                        <Typography className={classes.observationSubtitle}>{observation.modality ? MODALITY_OPTIONS_LABELS[observation.modality] : ''}</Typography>
                      </Box>

                      <Box display="flex" alignSelf="center" height="100%">
                        {completedObservations.has(index) && <CheckCircle color="primary" fontSize="small" />}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Quiz Collection Materials */}
            {materials.length > 0 && (
              <Box style={{ marginTop: 24 }}>
                <Typography style={{ fontSize: 18, fontWeight: 600, marginBottom: 16, textAlign: 'left' }}>Additional Materials</Typography>
                <MaterialsList materials={materials} selectedMaterials={materials} />
              </Box>
            )}
          </Box>
        </div>
      )}
    </QuizAccessGuard>
  );
};

export default QuizView;
